import React, { useState, useRef, useEffect, useCallback } from "react";

import addIcon from "../../assets/images/icons/addIcon.png";
import filterIcon from "../../assets/images/icons/filterIcon.svg";
import searchIcon from "../../assets/images/icons/search.png";
import DataTable from "../../components/common/DataTable/DataTable";
import FilterCoreModal from "../../components/common/FilterCoreModal/FilterCoreModal";
import FilterForm from "../../components/MyTasks/FilterForm/FilterForm";
import {
  useCreateLibraryTaskMutation,
  useGetLibraryTasksQuery,
  useUpdateLibraryTaskMutation,
  useDeleteLibraryTaskMutation,
  useGetTaskTypesQuery,
} from "./taskLibrarySlice";
import { nanoid } from "@reduxjs/toolkit";
import editIcon from "../../assets/images/icons/edit.png";
import IncidentReportingModal from "../../components/Incidents/IncidentReportingModal/IncidentReportingModal";
import DataForm from "../../components/common/DataForm/DataForm";
import { Loader } from "../../components/common/Loader/Loader";
import toast, { Toaster } from "react-hot-toast";
import deleteIcon from "../../assets/images/icons/Delete.svg";
import DeleteConfirmationModal from "../../components/common/DeleteConfirmationModal/DeleteConfirmationModal";
import { taskTypes as Types } from "../../enums/taskTypes";
import { sanitizeInputValue } from "../../utils/commonMethods";

const TaskLibrary = () => {
  let gridRef = useRef(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [columns, setColumns] = useState("");
  const [libraryTasksData, setLibraryTasksData] = useState([]);
  const [isUpdate, setIsUpdate] = useState(false);
  const [selectedData, setSelectedData] = useState({});

  const [formStructure, setFormStructure] = useState({
    pages: [
      {
        name: "page1",
        elements: [
          {
            type: "comment",
            name: "name",
            title: "Task Name",
            isRequired: true,
          },
          {
            type: "comment",
            name: "description",
            title: "Description",
            isRequired: true,
          },
          {
            type: "dropdown",
            name: "taskTypeId",
            title: "Task Type",
            isRequired: true,
            choices: [],
          },
          {
            type: "boolean",
            name: "isEmailNotificationEnabled",
            title: "Enable email notifications",
            defaultValue: "true",
          },
        ],
        title: "",
      },
    ],
  });

  const {
    data: libraryTasks,
    isLoading: libraryTasksIsLoading,
    isSuccess: libraryTasksIsSuccess,
    error: libraryTasksError,
  } = useGetLibraryTasksQuery();

  const {
    data: taskTypes,
    isLoading: taskTypesIsLoading,
    error: taskTypesError,
  } = useGetTaskTypesQuery();

  const generateColumns = useCallback((data) => {
    if (data.length <= 0) {
      return;
    }

    const optionsConfig = {
      key: nanoid(),
      field: "options",
      headerText: "Options",
      template: (props) => (
        <div>
          <button
            className="e-btn e-flat option-btn"
            onClick={() => {
              setSelectedData(props);
              setShowModal(true);
            }}
          >
            <img src={deleteIcon} alt="delete" width={30} />
          </button>
          <button
            className="e-btn e-flat option-btn"
            onClick={() => updateHandleClick(props)}
          >
            <img src={editIcon} alt="edit" width={30} />
          </button>
        </div>
      ),
      width: "200px",
    };

    const columns = Object.keys(data[0])
      .map((field) => {
        if (field === "id") {
          return null;
        }

        let columnConfig = {
          key: nanoid(),
          field,
          headerText: field
            .replace(/([A-Z])/g, " $1")
            .trim()
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
          alignment: "left",
          template: null,
          width: "180px",
        };

        if (field === "name") {
          columnConfig.template = (props) => {
            return <p className="m-0 fw-bold">{props[field]}</p>;
          };
          columnConfig.width = "400px";
        }
        if (field === "description") {
          columnConfig.width = "500px";
        }

        if (field === "taskType") {
          columnConfig.template = (props) => {
            return <p className="m-0 fw-bold">{props[field]?.name}</p>;
          };
        }
        if (field === "isEmailNotificationEnabled") {
          columnConfig.headerText = "Email Notification Enabled";
          columnConfig.template = (props) => {
            return (
              <p
                className={`status-pill ${
                  props[field] ? "yes-pill" : "no-pill"
                }`}
              >
                {props[field] ? "Yes" : "No"}
              </p>
            );
          };
        }

        return columnConfig;
      })
      .filter(Boolean);

    columns.splice(columns.length, 0, optionsConfig);
    return columns;
  }, []);

  useEffect(() => {
    if (libraryTasks) {
      const data = libraryTasks.ids.map((id) => {
        return libraryTasks.entities[id];
      });
      setLibraryTasksData(data);
      setColumns(generateColumns(data));
    }
    if (libraryTasksError) {
      toast.error(
        `${
          libraryTasksError?.data?.Message ||
          libraryTasksError?.data?.title ||
          "Unexpected error occured while fetching library tasks"
        }`
      );
    }
  }, [libraryTasks, libraryTasksError, generateColumns]);

  useEffect(() => {
    if (taskTypes) {
      const updatedFormStructure = { ...formStructure };
      const taskTypeDropdown = updatedFormStructure.pages[0].elements.find(
        (element) => element.name === "taskTypeId"
      );

      if (taskTypeDropdown) {
        taskTypeDropdown.defaultValue = Types.NORMAL;
        taskTypeDropdown.choices = taskTypes?.map((type) => ({
          value: type.id,
          text: type.name,
        }));
      }

      setFormStructure(updatedFormStructure);
    }
    if (taskTypesError) {
      toast.error(
        `${
          taskTypesError?.data?.Message ||
          taskTypesError?.data?.title ||
          "Unexpected error occured while fetching task types"
        }`
      );
    }
  }, [taskTypes, taskTypesError]);

  const [createLibraryTask, { isLoading: createLibraryTaskIsLoading }] =
    useCreateLibraryTaskMutation();

  const [updateLibraryTask, { isLoading: updateLibraryTaskIsLoading }] =
    useUpdateLibraryTaskMutation();

  const [deleteLibraryTask, { isLoading: deleteLibraryTaskIsLoading }] =
    useDeleteLibraryTaskMutation();

  const updateHandleClick = (props) => {
    setIsUpdate(true);
    setSelectedData({
      id: props?.id,
      description: props?.description,
      name: props?.name,
      isEmailNotificationEnabled: props?.isEmailNotificationEnabled,
      taskTypeId: props?.taskType?.id,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const openModal = () => {
    setIsUpdate(false);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    const response = await deleteLibraryTask(selectedData?.id).unwrap();
    if (response?.error) {
      toast.error(response?.error?.data?.title);
      return;
    }
    setShowModal(false);
    toast.success("Task Deleted Successful");
  };

  const handleSave = async (formData) => {
    if (isUpdate) {
      const response = await updateLibraryTask({
        data: formData,
        id: selectedData?.id,
      }).unwrap();
      if (response?.error) {
        toast.error(response?.error?.data?.title);
        return;
      }
      toast.success("Task Updated");
      closeModal();
    } else {
      const response = await createLibraryTask(formData).unwrap();
      if (response?.error) {
        toast.error(response?.error?.data?.title);
        return;
      }
      toast.success("Task Created");
      closeModal();
    }
  };

  const toggleModal = () => {
    setModalIsOpen(!modalIsOpen);
  };

  const handleSearch = (e) => {
    const grid = gridRef.current;
    const inputValue = sanitizeInputValue(e.target.value);
    grid?.search(inputValue);
  };

  return (
    <div className="task-libarary">
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#0b463d",
            color: "#ffffff",
          },
          success: {
            iconTheme: {
              secondary: "#ffffff",
            },
          },
        }}
      />
      <div className="row mb-1 justify-content-between align-items-center">
        <div className="col-auto">
          <h4 className="p-2 m-0">Task Library</h4>
        </div>
        <div className="col-auto">
          <button className="btn btn-green btn-icon px-5" onClick={openModal}>
            <span className="me-2"> New Task</span>
            <img src={addIcon} width={22} alt="" />
          </button>
        </div>
      </div>
      <div className="card custom-card incident-details-card">
        <div className="card-body">
          <div className="row mb-3">
            <div className="col">
              <div className="card custom-card img-card banner">
                <div className="">
                  <div className="card-body custom-card-body">
                    <p className="fs-2">Task Library</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row justify-content-between align-items-center mb-3">
            <div className="col-auto">
              <h6>Task List</h6>
            </div>
            <div className="col-auto">
              <div className="d-flex align-items-center">
                <div className="input-group">
                  <label
                    htmlFor="searchTask"
                    className="input-group-text search-icon-container"
                  >
                    <img
                      src={searchIcon}
                      alt="Search Icon"
                      className="icon-image"
                      width={12}
                    />
                  </label>
                  <input
                    id="searchTask"
                    type="text"
                    className="form-control search"
                    placeholder="Search Tasks"
                    onKeyUp={handleSearch}
                  />
                </div>
                <button className="btn btn-filter" onClick={toggleModal}>
                  <img src={filterIcon} width={34} alt="" />
                </button>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              {(libraryTasksIsLoading || taskTypesIsLoading) && <Loader />}
              {libraryTasksIsSuccess &&
                (libraryTasksData.length > 0 ? (
                  <DataTable
                    data={libraryTasksData}
                    columns={columns}
                    height={"calc(100vh - 555px)"}
                    grid={gridRef}
                  />
                ) : (
                  "No Tasks Available..."
                ))}
            </div>
          </div>
        </div>
      </div>
      <FilterCoreModal isOpen={modalIsOpen} toggle={toggleModal}>
        <FilterForm />
      </FilterCoreModal>
      <IncidentReportingModal isOpen={modalOpen} toggle={closeModal}>
        {createLibraryTaskIsLoading || updateLibraryTaskIsLoading ? (
          <div className="mb-5">
            <Loader />
          </div>
        ) : isUpdate ? (
          <DataForm
            data={formStructure}
            formData={selectedData}
            handleSave={handleSave}
            isUpdate={true}
          />
        ) : (
          <DataForm data={formStructure} handleSave={handleSave} />
        )}
      </IncidentReportingModal>
      <DeleteConfirmationModal
        type={"task"}
        showModal={showModal}
        closeModal={setShowModal}
        confirmDelete={handleDelete}
        loading={deleteLibraryTaskIsLoading}
      />
    </div>
  );
};

export default TaskLibrary;
